import { ArrowLeft, Music, Heart, Sparkles, Zap, PlayCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';

export default function DancePartyGuide() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-b from-pink-50 to-white">
      <div className="max-w-4xl mx-auto px-4 py-8">
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="mb-6 text-pink-700 hover:text-pink-800 hover:bg-pink-100"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Week
        </Button>

        <div className="bg-white rounded-2xl shadow-xl p-8 mb-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-3 bg-pink-100 rounded-full">
              <Music className="w-8 h-8 text-pink-600" />
            </div>
            <div>
              <h1 className="text-3xl font-serif font-bold text-gray-900">Dance Party</h1>
              <p className="text-pink-600">Week 11, Day 3 - Creative Expression</p>
            </div>
          </div>

          <div className="prose prose-pink max-w-none">
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              Put on 3-4 of your favorite songs and dance like nobody's watching! Close the curtains if 
              you need to. Move however your body wants to. Laugh at yourself. Feel the joy.
            </p>

            <div className="bg-pink-50 rounded-xl p-6 mb-8">
              <h2 className="text-xl font-semibold text-pink-800 mb-4 flex items-center gap-2">
                <Heart className="w-5 h-5" />
                Why Dancing is Magic
              </h2>
              <ul className="space-y-2 text-gray-700">
                <li>• Releases endorphins (instant mood boost)</li>
                <li>• Combines movement with creativity and music</li>
                <li>• Improves body image and self-acceptance</li>
                <li>• Provides joyful cardio exercise</li>
                <li>• Reconnects you with your body</li>
                <li>• Reduces stress and anxiety</li>
                <li>• Makes you feel alive and free</li>
              </ul>
            </div>

            <div className="bg-gradient-to-r from-pink-100 to-rose-100 rounded-xl p-6 mb-8">
              <h2 className="text-xl font-semibold text-pink-800 mb-4 flex items-center gap-2">
                <PlayCircle className="w-5 h-5" />
                Your Dance Party Guide
              </h2>
              <div className="space-y-4 text-gray-700">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-pink-500 text-white rounded-full flex items-center justify-center font-bold shrink-0">1</div>
                  <div>
                    <h3 className="font-semibold">Choose Your Songs</h3>
                    <p className="text-sm">Pick 3-4 songs that make you want to move. Upbeat, nostalgic, whatever speaks to you.</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-pink-500 text-white rounded-full flex items-center justify-center font-bold shrink-0">2</div>
                  <div>
                    <h3 className="font-semibold">Create Space</h3>
                    <p className="text-sm">Clear some floor space. Close curtains if that helps you feel free.</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-pink-500 text-white rounded-full flex items-center justify-center font-bold shrink-0">3</div>
                  <div>
                    <h3 className="font-semibold">Let Go</h3>
                    <p className="text-sm">There are no wrong moves. Shake, sway, jump, twirl - whatever feels good.</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-pink-500 text-white rounded-full flex items-center justify-center font-bold shrink-0">4</div>
                  <div>
                    <h3 className="font-semibold">Be Silly</h3>
                    <p className="text-sm">Laugh at yourself. Make weird faces. Enjoy the absurdity of it all.</p>
                  </div>
                </div>
              </div>
            </div>

            <h2 className="text-2xl font-serif font-bold text-gray-900 mb-4">Song Ideas by Mood</h2>
            
            <div className="grid md:grid-cols-2 gap-4 mb-8">
              <div className="bg-white border-2 border-pink-200 rounded-xl p-5">
                <h3 className="font-semibold text-pink-700 mb-2">Feel-Good Classics</h3>
                <ul className="text-gray-600 text-sm space-y-1">
                  <li>• "Dancing Queen" - ABBA</li>
                  <li>• "I Wanna Dance with Somebody" - Whitney Houston</li>
                  <li>• "Girls Just Want to Have Fun" - Cyndi Lauper</li>
                </ul>
              </div>
              
              <div className="bg-white border-2 border-pink-200 rounded-xl p-5">
                <h3 className="font-semibold text-pink-700 mb-2">Modern Anthems</h3>
                <ul className="text-gray-600 text-sm space-y-1">
                  <li>• "Happy" - Pharrell Williams</li>
                  <li>• "Uptown Funk" - Bruno Mars</li>
                  <li>• "Shake It Off" - Taylor Swift</li>
                </ul>
              </div>
              
              <div className="bg-white border-2 border-pink-200 rounded-xl p-5">
                <h3 className="font-semibold text-pink-700 mb-2">Empowerment</h3>
                <ul className="text-gray-600 text-sm space-y-1">
                  <li>• "Survivor" - Destiny's Child</li>
                  <li>• "Stronger" - Kelly Clarkson</li>
                  <li>• "Fight Song" - Rachel Platten</li>
                </ul>
              </div>
              
              <div className="bg-white border-2 border-pink-200 rounded-xl p-5">
                <h3 className="font-semibold text-pink-700 mb-2">High Energy</h3>
                <ul className="text-gray-600 text-sm space-y-1">
                  <li>• "Can't Stop the Feeling" - Justin Timberlake</li>
                  <li>• "Shut Up and Dance" - Walk the Moon</li>
                  <li>• "Don't Start Now" - Dua Lipa</li>
                </ul>
              </div>
            </div>

            <div className="bg-amber-50 rounded-xl p-6">
              <h2 className="text-xl font-semibold text-amber-800 mb-4 flex items-center gap-2">
                <Sparkles className="w-5 h-5" />
                Permission Slip
              </h2>
              <p className="text-gray-700 mb-4">
                You have full permission to:
              </p>
              <ul className="space-y-2 text-gray-700">
                <li>• Dance badly (there's no such thing)</li>
                <li>• Look ridiculous (embrace it)</li>
                <li>• Sing along loudly (bonus points)</li>
                <li>• Take breaks when needed</li>
                <li>• Dance in your socks, barefoot, or heels</li>
                <li>• Invite pets to join (they make great dance partners)</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
